import dayjs from 'dayjs';

import { DateState } from '@Components/DateRangePicker';
import { TableData, TableRow } from '@Components/Table';
import CommonConstants from '@Constants/common.constants';
import { StateTsrItem, StateTsrQuery } from '@Models/settings.models';
import { getDateMMDDYYYY } from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';

/**
 * Trims excluded days string in State TSR table
 */
const trimExcludedDaysString = (excludedDays: string): string => {
  if (excludedDays && excludedDays.length > 65) {
    return excludedDays.substring(0, 65).concat('...');
  }

  return excludedDays;
};

/**
 * Converts date string 2021-09-21 to 09/21/2021
 */
const getDisplayedExcludedDays = (excludedDays: string): string => {
  if (excludedDays) {
    const dateRanges = excludedDays.split(', ');
    const dateRangesModified = dateRanges
      .map((date) => {
        const [start, end] = date.split(' - ');
        if (start === end) {
          return getDateMMDDYYYY(new Date(start));
        }
        return `${getDateMMDDYYYY(new Date(start))} - ${getDateMMDDYYYY(
          new Date(end)
        )}`;
      })
      .join(', ');

    return trimExcludedDaysString(dateRangesModified);
  }

  return '';
};

const isStringDefined = (str: string): boolean =>
  str !== null && str !== undefined && str !== '';

/**
 * Converts string date to Date object
 */
export const getDateFromString = (dateStr: string | null): Date | null => {
  if (dateStr) {
    const format = dateStr.includes(':') ? 'h:mma' : 'ha';
    return dayjs(dateStr, format).toDate();
  }
  return null;
};

/**
 * Converts Date object to string
 */
export const getStringFromDate = (date: Date | null): string | null => {
  if (date) {
    const localTime = date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
    return localTime.toLowerCase().replace(/\s/g, '');
  }

  return null;
};

/**
 * Converts DateState[] to string with date ranges
 * for excludedDays field
 */
export const fromDateToDashedString = (
  dateRanges: DateState[]
): string | null => {
  const dateRangesStr = dateRanges
    .map(({ startDate, endDate }) => {
      if (startDate && endDate) {
        const yearStart = startDate.getFullYear();
        const monthStart = (startDate.getMonth() + 1)
          .toString()
          .padStart(2, '0');
        const dayStart = startDate.getDate().toString().padStart(2, '0');
        const formattedStartDate = `${yearStart}-${monthStart}-${dayStart}`;

        const yearEnd = endDate.getFullYear();
        const monthEnd = (endDate.getMonth() + 1).toString().padStart(2, '0');
        const dayEnd = endDate.getDate().toString().padStart(2, '0');
        const formattedEndDate = `${yearEnd}-${monthEnd}-${dayEnd}`;

        return `${formattedStartDate} - ${formattedEndDate}`;
      }
      return '';
    })
    .filter((dateRangeStr) => isStringDefined(dateRangeStr))
    .join(', ');

  return isStringDefined(dateRangesStr) ? dateRangesStr : null;
};

/**
 * Converts string with excludedDays to DateState[]
 * Used for filling the form while editing State TSR
 */
export const getExcludedDayRanges = (
  excludedDays: string | null
): DateState[] => {
  const defaultRange = [
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ];

  if (excludedDays) {
    const dateRanges = excludedDays.split(', ');

    if (dateRanges) {
      return dateRanges.map((date: string) => {
        const [start, end] = date.split(' - ');

        return {
          startDate: new Date(start),
          endDate: new Date(end),
          key: 'selection',
        };
      });
    }

    return defaultRange;
  }

  return defaultRange;
};

/**
 * Creates string for timeframe column in State TSR table
 */
const getDisplayedTimeFrame = (
  startTime: string | null,
  endTime: string | null
): string => {
  if (startTime && endTime) {
    return `${startTime.toUpperCase()} - ${endTime.toUpperCase()}`;
  }
  return 'No calls';
};

/**
 * Takes the passed parameters
 * Returns data for table row in desired format.
 */
export const createStateTsrData = (
  state: string,
  weekdays: string,
  saturday: string,
  sunday: string,
  excludedDays: string,
  actions?: React.ReactNode
): TableData => ({
  state,
  weekdays,
  saturday,
  sunday,
  excludedDays,
  actions,
});

/**
 * Returns Rows and Total Rows Count for Table Component.
 */
export const getStateTsrRows = (
  items: StateTsrItem[] = [],
  actions: (id: string, stateFullName: string) => JSX.Element
): TableRow[] => {
  const rows: TableRow[] = items.map((data) => ({
    id: data.id.toString(),
    data: createStateTsrData(
      data.stateFullName,
      getDisplayedTimeFrame(data.monFriStart, data.monFriEnd),
      getDisplayedTimeFrame(data.satStart, data.satEnd),
      getDisplayedTimeFrame(data.sunStart, data.sunEnd),
      getDisplayedExcludedDays(data.excludedDays),
      actions(data.id.toString(), data.stateFullName)
    ),
  }));

  return rows;
};

/**
 * RETURNS STATE TSR LIST RTK QUERY
 * @param search - URL Query Params
 * @returns RTK Query Parameters for STATE TSR LIST
 */
export const getStateTsrRTKQuery = (search: string): StateTsrQuery => {
  const queries = query.parse(search);

  return {
    search: queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string,
    order: queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string,
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    direction: queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string,
  };
};
